/** StampDuty.uk: SDLT bands computed in Double, as the Swift code does. */
import { fromInt, toDouble, type Decimal } from '../core/decimal.ts';

const FIRST_TIME_BUYER_NIL_BAND = 425_000;
const FIRST_TIME_BUYER_CEILING = 625_000;
const FIRST_TIME_BUYER_RATE = 5 / 100;

/** Lower bound and rate of each standard band; each applies up to the next bound. */
const STANDARD_BANDS: readonly { readonly from: number; readonly rate: number }[] = [
  { from: 0, rate: 0 / 100 },
  { from: 250_000, rate: 5 / 100 },
  { from: 925_000, rate: 10 / 100 },
  { from: 1_500_000, rate: 12 / 100 },
];

/** Double.rounded(): half away from zero. */
const roundHalfAway = (x: number): number => Math.sign(x) * Math.round(Math.abs(x));

const standardDuty = (price: number): number =>
  STANDARD_BANDS.reduce((duty, band, i) => {
    if (!(price > band.from)) return duty;
    const upper = STANDARD_BANDS[i + 1]?.from ?? Number.POSITIVE_INFINITY;
    return duty + (Math.min(price, upper) - band.from) * band.rate;
  }, 0);

/** First-time buyers pay 5% above the nil band (truncated) up to the ceiling; otherwise standard bands, rounded. */
export function ukStampDuty(price: Decimal, isFirstTimeBuyer: boolean): Decimal {
  const p = toDouble(price);
  if (isFirstTimeBuyer && p <= FIRST_TIME_BUYER_NIL_BAND) return fromInt(0);
  if (isFirstTimeBuyer && p <= FIRST_TIME_BUYER_CEILING) return fromInt(Math.trunc((p - FIRST_TIME_BUYER_NIL_BAND) * FIRST_TIME_BUYER_RATE));
  return fromInt(roundHalfAway(standardDuty(p)));
}
